import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  authService,
  AvailableLoginContextsResponse,
  CognitoUser,
  individualsService,
  LoginContext,
  setCognitoContext,
  setApiResponseInterceptor,
  useToast,
} from '@aslanpay/portal-common-lib';

import SsoActivation from 'app/components/Authentication/SsoActivation';
import FullscreenLoading from 'app/components/UI/FullscreenLoading';
import NotAuthorizedModal from 'app/components/UI/NotAuthorizedModal';
import useFetchCompany from 'app/hooks/useFetchCompany';
import useFetchIndividual from 'app/hooks/useFetchIndividual';
import useIsSSOUser from 'app/hooks/useIsSSOUser';
import useSignOut from 'app/hooks/useSignOut';
import {
  getActiveLoginContext,
  getPendingSsoContext,
  setActiveLoginContext,
} from 'app/constants/authentication';
import { requiresSsoActivation } from 'app/utils/ssoActivation';

import { AuthContextProps, AuthContextProviderProps } from './types';

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function AuthContextProvider({ children }: AuthContextProviderProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate: signOut, isLoading: isSigningOut } = useSignOut();
  // Seed from the pending SSO context on an SSO callback landing so the auth query targets the same
  // pool main.tsx already configured — otherwise it re-runs Auth.configure mid-exchange and the
  // callback times out.
  const [activeLoginContext, setCurrentLoginContext] = useState<LoginContext>(
    () => getPendingSsoContext() ?? getActiveLoginContext()
  );

  const { data: cognitoUser, isLoading: isValidatingAuth } = useQuery<CognitoUser>(
    ['getAuthenticatedUser', activeLoginContext],
    async () => {
      setCognitoContext(activeLoginContext);
      return authService.getAuthenticatedUser();
    }
  );

  /* Individual ID:
   - Password-based users: stored in Cognito user pool attributes
   - SSO (federated) users: attributes is undefined, so fall back to the ID token payload */
  const individualId =
    cognitoUser?.attributes?.['custom:individual_id'] ??
    cognitoUser?.signInUserSession?.idToken?.payload?.['custom:individual_id'];

  const { data: user, isInitialLoading: isFetchingIndividual } = useFetchIndividual(individualId);

  const { data: company, isInitialLoading: isLoadingCompany } = useFetchCompany(user?.companyId);

  const isSsoUser = useIsSSOUser(cognitoUser);
  const needsSsoActivation = requiresSsoActivation(user, isSsoUser);

  const { data: availableContextsResponse } = useQuery<AvailableLoginContextsResponse>(
    ['getAvailableLoginContexts', activeLoginContext, user?.email],
    () => individualsService.fetchAvailableLoginContexts(),
    { enabled: !!user }
  );

  const switchLoginContext = useCallback(
    async (context: LoginContext) => {
      if (context === activeLoginContext) {
        return true;
      }

      try {
        setCognitoContext(context);
        await authService.getAuthenticatedUser();
      } catch {
        setCognitoContext(activeLoginContext);
        return false;
      }

      setActiveLoginContext(context);
      setCurrentLoginContext(context);
      queryClient.invalidateQueries({ queryKey: ['getAuthenticatedUser'] });
      return true;
    },
    [activeLoginContext, queryClient]
  );

  const completeLoginContextAuthentication = useCallback(
    (context: LoginContext) => {
      setActiveLoginContext(context);
      setCurrentLoginContext(context);
      queryClient.invalidateQueries({ queryKey: ['getAuthenticatedUser'] });
    },
    [queryClient]
  );

  const value = useMemo(
    () => ({
      user,
      company,
      cognitoUser,
      isValidatingAuth,
      isFetchingIndividual,
      activeLoginContext,
      availableLoginContexts: availableContextsResponse?.contexts ?? [],
      switchLoginContext,
      completeLoginContextAuthentication,
    }),
    [
      user,
      company,
      cognitoUser,
      isValidatingAuth,
      isFetchingIndividual,
      activeLoginContext,
      availableContextsResponse?.contexts,
      switchLoginContext,
      completeLoginContextAuthentication,
    ]
  );

  useEffect(() => {
    if (user && company?.status === 'KYB_DECLINED' && !isSigningOut) {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Log in error',
        description: 'Please contact support@aslan.io if you have any questions.',
      });
      signOut();
    }
  }, [user, company?.status, toast, signOut, isSigningOut]);

  useEffect(() => {
    if (user) {
      setApiResponseInterceptor(() => onOpen());
    }
  }, [user, onOpen]);

  if (isValidatingAuth || isFetchingIndividual || isLoadingCompany) {
    return <FullscreenLoading />;
  }

  // If KYB is declined, show loading while signing out
  if (user && company?.status === 'KYB_DECLINED') {
    return <FullscreenLoading />;
  }

  return (
    <AuthContext.Provider value={value}>
      {needsSsoActivation && user ? <SsoActivation individual={user} /> : children}
      <NotAuthorizedModal isOpen={isOpen} onClose={onClose} />
    </AuthContext.Provider>
  );
}
